import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AnticipatedExpenditureSelectionModel, ReportURLResponseModel } from "services/api/cplapi/CPLAPI";
import { IAnticipatedExpenditureReportService } from "services/AnticipatedExpenditureReportService";
import { RootState } from "setup";
import { container } from "setup/dependency/inversify.config";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { toast } from "react-toastify";

const initialState: {
  ReportPreviewUrl: ReportURLResponseModel;
  Loading: boolean;
} = {
  ReportPreviewUrl: new ReportURLResponseModel(),
  Loading: false,
};

export const anticipatedExpenditureSlice = createSlice({
  name: "anticipatedExpenditure",
  initialState,
  reducers: {
    resetData: (state) => {
      state.ReportPreviewUrl = new ReportURLResponseModel();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(resumeAnticipatedExpenditureProcess.pending, (state) => {
        state.Loading = true;
      })
      .addCase(resumeAnticipatedExpenditureProcess.fulfilled, (state) => {
        state.Loading = false;
      })
      .addCase(resumeAnticipatedExpenditureProcess.rejected, (state) => {
        state.Loading = false;
      })
      .addCase(resetAnticipatedExpenditureProcess.pending, (state) => {
        state.Loading = true;
      })
      .addCase(resetAnticipatedExpenditureProcess.fulfilled, (state) => {
        state.Loading = false;
      })
      .addCase(resetAnticipatedExpenditureProcess.rejected, (state) => {
        state.Loading = false;
      })
      .addCase(previewAnticipatedExpenditureReport.pending, (state) => {
        state.Loading = true;
      })
      .addCase(previewAnticipatedExpenditureReport.rejected, (state) => {
        state.ReportPreviewUrl = new ReportURLResponseModel();
        state.Loading = false;
      })
      .addCase(previewAnticipatedExpenditureReport.fulfilled, (state, action) => {
        if (action.payload.previewURL == null) {
          toast.error("No data is available based on your selections.");
          state.ReportPreviewUrl = new ReportURLResponseModel();
        } else {
          state.ReportPreviewUrl = action.payload;
        }
        state.Loading = false;
      })
      .addCase(acceptAnticipatedExpenditureReport.pending, (state) => {
        state.Loading = true;
      })
      .addCase(acceptAnticipatedExpenditureReport.rejected, (state) => {
        state.Loading = false;
      })
      .addCase(acceptAnticipatedExpenditureReport.fulfilled, (state) => {
        state.Loading = false;
      })
      .addCase(printAnticipatedExpenditureReport.pending, (state) => {
        state.Loading = true;
      })
      .addCase(printAnticipatedExpenditureReport.rejected, (state) => {
        state.Loading = false;
      })
      .addCase(printAnticipatedExpenditureReport.fulfilled, (state, action) => {
        state.ReportPreviewUrl = action.payload;
        state.Loading = false;
      })
      .addCase(emailAnticipatedExpenditureReport.pending, (state) => {
        state.Loading = true;
      })
      .addCase(emailAnticipatedExpenditureReport.rejected, (state) => {
        state.Loading = false;
      })
      .addCase(emailAnticipatedExpenditureReport.fulfilled, (state, action) => {
        state.ReportPreviewUrl = action.payload;
        state.Loading = false;
      });
  },
});

export const resumeAnticipatedExpenditureProcess = createAsyncThunk(
  "AnticipatedExpenditure/ResumeProcess",
  async () => {
    const anticipatedExpenditureService = container.get<IAnticipatedExpenditureReportService>(
      SERVICE_TYPES.AnticipatedExpenditureReportService
    );
    return await anticipatedExpenditureService.resumeProcess();
  }
);

export const resetAnticipatedExpenditureProcess = createAsyncThunk("AnticipatedExpenditure/ResetProcess", async () => {
  const anticipatedExpenditureService = container.get<IAnticipatedExpenditureReportService>(
    SERVICE_TYPES.AnticipatedExpenditureReportService
  );
  await anticipatedExpenditureService.resetAnticipatedExpenditureProcess();
});

export const previewAnticipatedExpenditureReport = createAsyncThunk<
  ReportURLResponseModel,
  { reportSelections: AnticipatedExpenditureSelectionModel }
>("AnticipatedExpenditure/GetAnticipatedExpenditureReport", async ({ reportSelections }) => {
  const anticipatedExpenditureService = container.get<IAnticipatedExpenditureReportService>(
    SERVICE_TYPES.AnticipatedExpenditureReportService
  );
  return await anticipatedExpenditureService.previewAnticipatedExpenditureReport(reportSelections);
});

export const acceptAnticipatedExpenditureReport = createAsyncThunk<
  void,
  { reportSelections: AnticipatedExpenditureSelectionModel }
>("AnticipatedExpenditure/AcceptAnticipatedExpenditureReport", async ({ reportSelections }) => {
  const anticipatedExpenditureService = container.get<IAnticipatedExpenditureReportService>(
    SERVICE_TYPES.AnticipatedExpenditureReportService
  );
  return await anticipatedExpenditureService.acceptAnticipatedExpenditureReport(reportSelections);
});

export const printAnticipatedExpenditureReport = createAsyncThunk<
  ReportURLResponseModel,
  { reportSelections: AnticipatedExpenditureSelectionModel }
>("AnticipatedExpenditure/PrintAnticipatedExpenditureReport", async () => {
  const anticipatedExpenditureService = container.get<IAnticipatedExpenditureReportService>(
    SERVICE_TYPES.AnticipatedExpenditureReportService
  );
  return await anticipatedExpenditureService.printAnticipatedExpenditureReport();
});

export const emailAnticipatedExpenditureReport = createAsyncThunk<
  ReportURLResponseModel,
  { reportSelections: AnticipatedExpenditureSelectionModel }
>("AnticipatedExpenditure/EmailAnticipatedExpenditureReport", async () => {
  const anticipatedExpenditureService = container.get<IAnticipatedExpenditureReportService>(
    SERVICE_TYPES.AnticipatedExpenditureReportService
  );
  return await anticipatedExpenditureService.emailAnticipatedExpenditureReport();
});

export const selectPreviewAEReport = (state: RootState) => {
  return state.anticipatedExpenditure.ReportPreviewUrl;
};
export const selectLoading = (state: RootState) => {
  return state.anticipatedExpenditure.Loading;
};

export const { resetData } = anticipatedExpenditureSlice.actions;

export default anticipatedExpenditureSlice.reducer;
